import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Download, Send } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import Tabs from '../../../components/ui/Tabs';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import { Badge } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Note } from '../../../components/performance/evaluation/EvaluationParts';
import {
  downloadBlob, formatNum, formatSigned, gapTone, profileLink,
} from '../../../components/performance/evaluation/evaluationAnalytics';
import {
  NEGATIVE_BANDS, NS, VIEWS, exportRecords, fetchRecords, inviteToIdp,
} from '../config';

// E05 alt sağ — yetkinlik tablosu: rol, yetkinlik, beklenen, mevcut, fark, değerlendirici grubu ve plan bağlantısı. Liste =
// EmployeesPage aynası (DataTable, sunucu tarafı sayfalama, başlık yalnız tablo başlığında). Pano sayfasında tablo kendi içinde
// KAYMAZ (tek kaydırma: sayfa). Görünümler: Açıklar | Beklenen Seviyesi Yok | Puanlanmamış (veri eksik listesi; analiz §8 kabul
// ölçütü). Satır → E14 yetkinlik sekmesi. İK + TD + TD.Write: taslak gelişim planı daveti (E12 akışı; satır ya da sayfadakiler).
const Wrap = styled.div`
  grid-column: span 12;
  min-width: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const GapText = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ $color }) => $color};
`;

const SORT_OF_COLUMN = { fullName: 'employee', competencyName: 'competency', gap: 'gap' };

export default function CompetencyRecords({
  filters, screen, view, onView, summary, context, canInvite,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const lang = i18n.language;
  const resetKey = JSON.stringify([filters, screen, view]);
  const [table, setTable] = useState({ resetKey, pageNumber: 1, pageSize: 10, sort: null, order: 'asc' });
  const [invite, setInvite] = useState(null);   // { employeeIds, label }
  const [inviting, setInviting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Süzgeç/görünüm değişince ilk sayfaya dönülür (eski sayfa numarası yeni kümede boş sayfa açmasın).
  const effective = table.resetKey === resetKey ? table : { resetKey, pageNumber: 1, pageSize: table.pageSize, sort: null, order: 'asc' };
  const tableKey = JSON.stringify(effective);
  const panel = usePanel(() => fetchRecords(filters, screen, {
    view, sort: effective.sort, order: effective.order, pageNumber: effective.pageNumber, pageSize: effective.pageSize,
  }), [tableKey], !!filters.periodId);
  const rows = panel.data?.data || [];
  const total = panel.data?.totalRowCount || 0;
  const tdVisible = !!(panel.data?.context?.tdVisible ?? context?.tdVisible);

  const onTableChanged = (action, state) => {
    const sortName = state.sortOrder?.name;
    setTable({
      resetKey,
      pageNumber: (state.page || 0) + 1,
      pageSize: state.rowsPerPage || 10,
      sort: sortName ? SORT_OF_COLUMN[sortName] || null : null,
      order: state.sortOrder?.direction || 'asc',
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportRecords(filters, screen, { view, sort: effective.sort, order: effective.order });
      downloadBlob(res, 'yetkinlik-aciklari.xlsx');
    } finally {
      setExporting(false);
    }
  };

  const confirmInvite = async () => {
    setInviting(true);
    const ok = await inviteToIdp(invite.employeeIds);
    setInviting(false);
    if (ok) setInvite(null);
  };

  // Sayfadaki açığı olan ve aktif planı olmayan kişiler (tekil) — toplu davet adayı.
  const pageCandidates = [...new Set(rows
    .filter((r) => r.hasActivePlan === false && NEGATIVE_BANDS.includes(r.gapBand))
    .map((r) => r.employeeId))];

  const rowOf = (meta) => rows[meta.rowIndex] || {};
  const dash = '-';
  const tabs = VIEWS.map((v) => ({
    key: v,
    label: v === 'noExpected' && summary ? `${t(`${NS}.view.${v}`)} (${formatNum(summary.recordsWithoutExpected, lang, 0)})`
      : v === 'notScored' && summary ? `${t(`${NS}.view.${v}`)} (${formatNum(summary.requiredNotScored, lang, 0)})`
        : t(`${NS}.view.${v}`),
  }));

  const columns = [
    {
      name: 'fullName',
      label: t(`${NS}.col.employee`),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => (
          <Link to={profileLink(rowOf(meta).employeeId, filters.periodId, 'competencies')} onClick={(e) => e.stopPropagation()} data-link="profile">{v}</Link>
        ),
      },
    },
    { name: 'departmentName', label: t(`${NS}.col.department`), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'titleName', label: t(`${NS}.col.title`), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    {
      name: 'competencyName',
      label: t(`${NS}.col.competency`),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          return (
            <span>
              {v}
              {r.competencyGroup ? <Muted>{` · ${r.competencyGroup}`}</Muted> : null}
            </span>
          );
        },
      },
    },
    { name: 'expected', label: t(`${NS}.col.expected`), options: { sort: false, filter: false, customBodyRender: (v) => (v === null || v === undefined ? dash : formatNum(v, lang, 0)) } },
    { name: 'current', label: t(`${NS}.col.current`), options: { sort: false, filter: false, customBodyRender: (v) => (v === null || v === undefined ? dash : formatNum(v, lang)) } },
    {
      name: 'gap',
      label: t(`${NS}.col.gap`),
      options: {
        sort: view === 'gaps', filter: false,
        customBodyRender: (v, meta) => {
          if (v === null || v === undefined) return dash;
          const r = rowOf(meta);
          return (
            <span>
              <GapText $color={gapTone(theme, v)} data-gap={r.employeeId}>{formatSigned(v, lang)}</GapText>
              {' '}
              <Badge $tone={v < 0 ? 'danger' : 'success'}>{t(`${NS}.gapBand.${r.gapBand}`)}</Badge>
            </span>
          );
        },
      },
    },
    {
      name: 'raterGroups',
      label: t(`${NS}.col.raterGroups`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v) => ((v || []).length ? (v || []).map((g) => t(`${NS}.raterGroup.${g}`)).join(', ') : dash),
      },
    },
    ...(view === 'gaps' ? [] : [{
      name: 'missingReason',
      label: t(`${NS}.col.missingReason`),
      options: { sort: false, filter: false, customBodyRender: (v) => (v ? t(`${NS}.missing.${v}`) : dash) },
    }]),
    ...(tdVisible ? [{
      name: 'hasActivePlan',
      label: t(`${NS}.col.plan`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v) => (v === true ? <Badge $tone="success">{t(`${NS}.plan.active`)}</Badge> : <Muted>{t(`${NS}.plan.none`)}</Muted>),
      },
    }] : []),
    {
      name: 'participantId',
      label: t(`${NS}.col.actions`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          const actions = [
            { type: 'open', label: t(`${NS}.rowAction.profile`), onClick: () => history.push(profileLink(r.employeeId, filters.periodId, 'competencies')) },
          ];
          if (canInvite && tdVisible && r.hasActivePlan === false) {
            actions.push({
              type: 'send', label: t(`${NS}.rowAction.inviteIdp`),
              onClick: () => setInvite({ employeeIds: [r.employeeId], label: r.fullName }),
            });
          }
          return <OptionsMenu row={r} actions={actions} />;
        },
      },
    },
  ];

  return (
    <Wrap data-panel="records">
      <Tabs tabs={tabs} activeTab={view} onChange={onView} />
      {view !== 'gaps' ? <Note>{t(`${NS}.view.${view}Hint`)}</Note> : null}
      {panel.error ? (
        <>
          <LoadErrorBanner message={t('performance.operations.panel.error')} />
          <Button variant="outline" onClick={panel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
        </>
      ) : null}
      <DataTable
        title={t(`${NS}.list.title`)}
        fetching={panel.loading}
        columns={columns}
        data={rows}
        totalRowCount={total}
        pageIndex={effective.pageNumber - 1}
        pageSize={effective.pageSize}
        onTableChanged={onTableChanged}
        onRowClick={(row) => history.push(profileLink(row.employeeId, filters.periodId, 'competencies'))}
        customIdSelector="participantId"
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={(
          <>
            {canInvite && tdVisible && view === 'gaps' ? (
              <Button
                variant="outline" disabled={pageCandidates.length === 0} data-action="invite-page"
                onClick={() => setInvite({ employeeIds: pageCandidates, label: null })}
              >
                <Send size={14} />
                {' '}
                {t(`${NS}.list.invitePage`, { count: pageCandidates.length })}
              </Button>
            ) : null}
            <Button variant="outline" onClick={handleExport} disabled={exporting || total === 0} data-action="export">
              <Download size={14} />
              {' '}
              {exporting ? t('common.loading') : t(`${NS}.list.export`)}
            </Button>
          </>
        )}
      />
      <ConfirmDialog
        isOpen={!!invite}
        type="info"
        title={t(`${NS}.invite.title`)}
        message={invite?.label
          ? t(`${NS}.invite.messageOne`, { name: invite.label })
          : t(`${NS}.invite.messageMany`, { count: invite?.employeeIds?.length || 0 })}
        confirmText={t(`${NS}.invite.confirm`)}
        cancelText={t('common.cancel')}
        loading={inviting}
        onConfirm={confirmInvite}
        onCancel={() => setInvite(null)}
      />
    </Wrap>
  );
}
